import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import './Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState('');
  const messagesEndRef = useRef(null);

  // Fetch knowledge base on mount
  useEffect(() => {
    fetch('/knowledge_base.txt')
      .then(response => response.text())
      .then(text => setKnowledgeBase(text))
      .catch(error => console.error("Failed to load knowledge base:", error));
  }, []);

  // Set initial welcome message
  useEffect(() => {
    setMessages([
      {
        text: "Hello! I'm Mridul's AI assistant. Ask me anything about his skills, experience, or projects.",
        sender: 'bot'
      }
    ]);
  }, []);

  // Auto-scroll to the latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  const handleUserInput = (e) => {
    setUserInput(e.target.value);
  };

  const handleSendClick = () => {
    handleSendMessage();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    if (userInput.trim() === '') return;

    const userMessage = userInput;
    // Add user message to chat
    setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);
    setUserInput('');
    setIsLoading(true);

    // Prepare history: map current messages to {role, parts: [{text}]} format
    // Filter out error messages or 'typing' placeholders if any (though state messages are clean strings here)
    // We limit to last 10 to check memory usage, or let backend handle simple text list.
    // Let's send a simplified list of objects and let backend format for Gemini SDK.
    const history = messages
      .filter(msg => !msg.isError) // assuming we might mark errors later, or just filter known bad strings? actually simpler:
      .slice(-10) // Last 10 messages
      .map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'model',
        parts: [{ text: msg.text }]
      }));

    try {
      const response = await fetch('/.netlify/functions/getGeminiResponse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: userMessage,
          context: knowledgeBase,
          history: history
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        console.error("Failed to parse error response:", e);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      setMessages(prev => [...prev, { text: data.reply, sender: 'bot' }]);

    } catch (error) {
      console.error("Error getting bot response:", error);
      // Display the actual error message from the backend if available
      const errorMessage = error.message || "Sorry, something went wrong. Please try again.";
      setMessages(prev => [...prev, { text: errorMessage, sender: 'bot' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chatbot-container">
      <div className={`chatbot-launcher ${isOpen ? 'open' : ''}`} onClick={toggleChatbot}>
        <i className={isOpen ? 'fas fa-times' : 'fas fa-comments'}></i>
      </div>
      <div className={`chatbot-window ${isOpen ? 'open' : ''}`}>
        <div className="chatbot-header">
          <h3>AI Assistant</h3>
          <p>Powered by Gemini</p>
          <span className="close-btn" onClick={toggleChatbot}>&times;</span>
        </div>
        <div className="chatbot-messages">
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.sender}`}>
              {msg.sender === 'bot' ? (
                <ReactMarkdown>{msg.text}</ReactMarkdown>
              ) : (
                msg.text
              )}
            </div>
          ))}
          {isLoading && <div className="message bot typing">...</div>}
          <div ref={messagesEndRef} />
        </div>
        <div className="chatbot-input">
          <input
            type="text"
            placeholder="Ask a question..."
            value={userInput}
            onChange={handleUserInput}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button className="send-btn" onClick={handleSendClick} disabled={isLoading}>
            <i className="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  );
};


export default Chatbot;
